import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { MultipleReveal } from '../../questions/MultipleReveal';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const MULTIPLE_REVEAL_TRACKED_IDS_KEY = 'multiple_reveal_flipped_option_ids';

const normalizeIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<number>();
  const normalized: number[] = [];

  value.forEach((entry) => {
    const numeric = typeof entry === 'number' ? entry : Number(entry);
    if (Number.isFinite(numeric) && !seen.has(numeric)) {
      seen.add(numeric);
      normalized.push(numeric);
    }
  });

  return normalized;
};

const getTrackedOptionIdsFromMetadata = (
  metadata: ResponsePayload['metadata'] | undefined,
): number[] => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return [];
  }

  return normalizeIds(metadata[MULTIPLE_REVEAL_TRACKED_IDS_KEY]);
};

type MultipleRevealState = {
  currentlyRevealed: number[];
  flippedAtLeastOnce: number[];
};

const MultipleRevealRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const optionIds = useMemo(
    () =>
      Array.isArray(question?.options)
        ? question.options
            .map((option) => option?.id)
            .filter((id): id is number => typeof id === 'number')
        : [],
    [question?.options],
  );
  const optionIdSet = useMemo(() => new Set(optionIds), [optionIds]);

  const sanitizeForQuestionOptions = useCallback(
    (ids: number[]) => ids.filter((id) => optionIdSet.has(id)),
    [optionIdSet],
  );

  const initialRevealed = useMemo(
    () => sanitizeForQuestionOptions(normalizeIds(existingResponse?.revealed_options)),
    [existingResponse?.revealed_options, sanitizeForQuestionOptions],
  );
  const initialFlippedAtLeastOnce = useMemo(() => {
    const fromMetadata = sanitizeForQuestionOptions(
      getTrackedOptionIdsFromMetadata(existingResponse?.metadata),
    );
    if (fromMetadata.length > 0) {
      return fromMetadata;
    }

    return initialRevealed;
  }, [existingResponse?.metadata, initialRevealed, sanitizeForQuestionOptions]);

  const [revealState, setRevealState] = useState<MultipleRevealState>({
    currentlyRevealed: initialRevealed,
    flippedAtLeastOnce: initialFlippedAtLeastOnce,
  });
  const lastHydratedSignatureRef = useRef<string | null>(null);
  const metadataWithoutTracking = useMemo(() => {
    if (!existingResponse?.metadata || typeof existingResponse.metadata !== 'object' || Array.isArray(existingResponse.metadata)) {
      return {};
    }

    const nextMetadata = { ...existingResponse.metadata };
    delete nextMetadata[MULTIPLE_REVEAL_TRACKED_IDS_KEY];
    return nextMetadata;
  }, [existingResponse?.metadata]);

  const emit = useCallback(
    (nextCurrentlyRevealed: number[], nextFlippedAtLeastOnce: number[]) => {
      const normalizedCurrent = sanitizeForQuestionOptions(normalizeIds(nextCurrentlyRevealed));
      const normalizedFlipped = sanitizeForQuestionOptions(normalizeIds(nextFlippedAtLeastOnce));
      const hasReveals = normalizedFlipped.length > 0;
      const flippedSet = new Set(normalizedFlipped);
      const allCardsFlippedAtLeastOnce =
        optionIds.length === 0 || optionIds.every((id) => flippedSet.has(id));

      onHasUserInputChange?.(hasReveals);
      onValidityChange?.(allCardsFlippedAtLeastOnce);

      if (questionId < 0) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        revealed_options: normalizedCurrent,
        metadata: {
          ...metadataWithoutTracking,
          [MULTIPLE_REVEAL_TRACKED_IDS_KEY]: normalizedFlipped,
        },
      };

      onDraftChange(payload);
    },
    [
      metadataWithoutTracking,
      onDraftChange,
      onHasUserInputChange,
      onValidityChange,
      optionIds,
      questionId,
      sanitizeForQuestionOptions,
    ],
  );

  useEffect(() => {
    const signature = JSON.stringify({
      currentlyRevealed: initialRevealed,
      flippedAtLeastOnce: initialFlippedAtLeastOnce,
    });
    if (signature === lastHydratedSignatureRef.current) {
      return;
    }

    lastHydratedSignatureRef.current = signature;
    const hydratedState: MultipleRevealState = {
      currentlyRevealed: initialRevealed,
      flippedAtLeastOnce: initialFlippedAtLeastOnce,
    };
    setRevealState(hydratedState);
  }, [emit, initialFlippedAtLeastOnce, initialRevealed]);

  useEffect(() => {
    emit(revealState.currentlyRevealed, revealState.flippedAtLeastOnce);
  }, [emit, revealState]);

  const handleToggle = (optionId: number) => {
    setRevealState((previousState) => {
      const wasCurrentlyRevealed = previousState.currentlyRevealed.includes(optionId);
      const nextCurrentlyRevealed = wasCurrentlyRevealed
        ? previousState.currentlyRevealed.filter((id) => id !== optionId)
        : [...previousState.currentlyRevealed, optionId];
      const hasBeenFlipped = previousState.flippedAtLeastOnce.includes(optionId);
      const nextFlippedAtLeastOnce = hasBeenFlipped
        ? previousState.flippedAtLeastOnce
        : [...previousState.flippedAtLeastOnce, optionId];

      return {
        currentlyRevealed: nextCurrentlyRevealed,
        flippedAtLeastOnce: nextFlippedAtLeastOnce,
      };
    });
  };

  return (
    <MultipleReveal
      options={question?.options}
      revealedOptions={revealState.currentlyRevealed}
      flippedOptionIds={revealState.flippedAtLeastOnce}
      setRevealedOptions={handleToggle}
    />
  );
};

registerQuestionRenderer(['multiple_reveal'], MultipleRevealRenderer);
